import { Controller } from "@hotwired/stimulus"
import { Dropdown } from "bootstrap"
import { computePosition, flip, offset, shift } from "@floating-ui/dom"
import { DirectUpload } from "@rails/activestorage"
import {
  LINK_ATTRIBUTE_ALLOWLIST,
  applyLinkToRange,
  cloneLiveRange,
  linkFromRange,
  rangeIsInRoot
} from "../lib/rich_text_link"

// The CSS weight from which the browser treats text as bold — the threshold its
// own bold state and bold command are judged against.
const BOLD_WEIGHT_THRESHOLD = 600

// Block-level tags the editor treats as a mergeable/list-triggering text block.
const TEXT_BLOCK_TAGS = ['P', 'H1', 'H2', 'H3', 'H4', 'DIV']

// Mirrors Lxp::CasePresentationNote::MAX_IMAGE_SIZE / ALLOWED_IMAGE_CONTENT_TYPES, so an image the
// server would reject is refused here instead of after a pointless round trip to S3.
const MAX_IMAGE_BYTES = 15 * 1024 * 1024
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"]

// Rich Text Editor Controller
// Provides basic WYSIWYG editing with toolbar and placeholder support.
// With bubbleToolbar enabled the toolbar floats above the text selection
// (Notion/Docs style) and is hidden while nothing is selected.
export default class extends Controller {
  static targets = [
    "editor",
    "input",
    "backgroundSwatch",
    "textColorSwatch",
    "linkToggle",
    "linkUrl",
    "linkNewTab",
    "imageInput",
    "imageStatus",
    "imageButton"
  ]
  static values = { bubbleToolbar: Boolean, images: Boolean, directUploadUrl: String }

  connect() {
    this.handleEditorInput = this.handleEditorInput.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handlePaste = this.handlePaste.bind(this)
    this.handleFocus = this.handleFocus.bind(this)
    this.handleBlur = this.handleBlur.bind(this)
    this.disarmBoundaryMergeOnClick = this.disarmBoundaryMergeOnClick.bind(this)
    this.refreshToolbarState = this.refreshToolbarState.bind(this)

    this.editorTarget.addEventListener('input', this.handleEditorInput)
    this.editorTarget.addEventListener('keydown', this.handleKeydown)
    this.editorTarget.addEventListener('paste', this.handlePaste)
    this.editorTarget.addEventListener('focus', this.handleFocus)
    this.editorTarget.addEventListener('blur', this.handleBlur)
    this.editorTarget.addEventListener('keyup', this.refreshToolbarState)
    this.editorTarget.addEventListener('mouseup', this.refreshToolbarState)
    // A click that moves the caret elsewhere inside the (still-focused) editor fires neither
    // 'input' nor 'blur' — without this, a boundary armed by one Backspace stays armed and
    // fires on an unrelated later visit back to the same spot. Keydown-based navigation (arrow
    // keys, typing) is covered by handleBlockBoundaryMerge/handleEditorInput disarming instead.
    this.editorTarget.addEventListener('mouseup', this.disarmBoundaryMergeOnClick)
    document.addEventListener('selectionchange', this.refreshToolbarState)

    if (this.bubbleToolbarValue) {
      this.handlePointerDown = this.handlePointerDown.bind(this)
      this.handlePointerUp = this.handlePointerUp.bind(this)
      this.editorTarget.addEventListener('pointerdown', this.handlePointerDown)
      document.addEventListener('pointerup', this.handlePointerUp)
    }
    
    // Lift a saved container tint out of the loaded value before anything else
    // reads the editor content, then initialize empty state + toolbar styling.
    this.hydrateBackground()
    this.updatePlaceholderState()
    this.refreshToolbarState()
    this.initDropdowns()
    this.bindLinkMenu()
  }

  get toolbarElement() {
    return this.element.querySelector('.rich-text-toolbar')
  }

  disconnect() {
    if (this.boundaryMergeHistoryTimer) clearTimeout(this.boundaryMergeHistoryTimer)

    this.editorTarget.removeEventListener('input', this.handleEditorInput)
    this.editorTarget.removeEventListener('keydown', this.handleKeydown)
    this.editorTarget.removeEventListener('paste', this.handlePaste)
    this.editorTarget.removeEventListener('focus', this.handleFocus)
    this.editorTarget.removeEventListener('blur', this.handleBlur)
    this.editorTarget.removeEventListener('keyup', this.refreshToolbarState)
    this.editorTarget.removeEventListener('mouseup', this.refreshToolbarState)
    this.editorTarget.removeEventListener('mouseup', this.disarmBoundaryMergeOnClick)
    document.removeEventListener('selectionchange', this.refreshToolbarState)

    if (this.bubbleToolbarValue) {
      this.editorTarget.removeEventListener('pointerdown', this.handlePointerDown)
      document.removeEventListener('pointerup', this.handlePointerUp)
    }

    this.dropdowns?.forEach((dropdown) => dropdown.dispose())
    this.dropdowns = null

    if (this.hasLinkToggleTarget && this.handleLinkMenuShown) {
      this.linkToggleTarget.removeEventListener('show.bs.dropdown', this.handleLinkMenuShow)
      this.linkToggleTarget.removeEventListener('shown.bs.dropdown', this.handleLinkMenuShown)
    }

    this.toolbarElement?.removeEventListener('mousedown', this.handleLinkMenuPointerDown, true)
  }

  // The editor container clips its overflow to keep its rounded corners, which
  // would otherwise slice off the toolbar/footer dropdown popovers (background
  // color, Add Placeholder). Pre-create their Bootstrap instances with the fixed
  // popper strategy so each menu is positioned against the viewport and escapes
  // that clip. Pre-creating (before the data-API's first click) makes the click
  // reuse this configured instance rather than a default one.
  initDropdowns() {
    this.dropdowns = Array.from(
      this.element.querySelectorAll('[data-bs-toggle="dropdown"]')
    ).map((toggle) => Dropdown.getOrCreateInstance(toggle, {
      popperConfig: (defaultConfig) => ({ ...defaultConfig, strategy: "fixed" })
    }))
  }

  bindLinkMenu() {
    if (!this.hasLinkToggleTarget) return

    this.handleLinkMenuShow = this.handleLinkMenuShow.bind(this)
    this.handleLinkMenuShown = this.handleLinkMenuShown.bind(this)
    this.handleLinkMenuPointerDown = this.handleLinkMenuPointerDown.bind(this)
    this.linkToggleTarget.addEventListener('show.bs.dropdown', this.handleLinkMenuShow)
    this.linkToggleTarget.addEventListener('shown.bs.dropdown', this.handleLinkMenuShown)
    this.toolbarElement?.addEventListener('mousedown', this.handleLinkMenuPointerDown, true)
  }

  // Capture phase runs before the button takes focus, so the highlight is still
  // live. Waiting until show.bs.dropdown is too late — the caret has already
  // collapsed into the toolbar.
  handleLinkMenuPointerDown(event) {
    if (!event.target.closest('.link-dropdown, .link-menu')) return

    this.saveLinkSelection()
  }

  handleEditorInput(event) {
    this.armedBoundaryMerge = null
    if (this.preparingBoundaryMerge || this.coalescingBoundaryMergeHistory) return
    if (this.coalesceBoundaryMergeHistory(event)) return

    this.boundaryMergeHistory = null
    this.syncContentChange()
  }

  handleKeydown(event) {
    if (this.handleBlockBoundaryMerge(event)) return
    if (this.applyListInputRule(event)) return

    const action = this.shortcutAction(event)
    if (action) this[action](event)
  }

  // Backspace/Delete at a block boundary is left to the browser's native contenteditable merge by
  // default, which folds the surviving block into whichever tag came FIRST — so backspacing a
  // paragraph up into a heading silently turns the whole paragraph into that heading's size. Chrome
  // also preserves the moved block's computed appearance in an inline style span, so the live editor
  // and the server-sanitized value disagree after reload. Crossing a DIFFERENTLY STYLED boundary is
  // therefore deliberate: the first Backspace/Delete is absorbed as a no-op. Before the second
  // merge, the heading block is changed to a paragraph and the two blocks are replaced through
  // browser editing commands. This avoids Chrome's style-preserving native boundary deletion while
  // keeping the whole operation on the contenteditable undo stack. Same-style merges remain fully
  // native on the first press.
  handleBlockBoundaryMerge(event) {
    const isBackspace = event.key === 'Backspace'
    if (!isBackspace && event.key !== 'Delete') return this.disarmBoundaryMerge(false)
    if (event.isComposing) return false

    const range = this.collapsedCaretRange()
    if (!range) return this.disarmBoundaryMerge(false)

    const block = this.enclosingTextBlock(range)
    if (!block || !this.isMergeableBlock(block)) return this.disarmBoundaryMerge(false)

    const neighbor = isBackspace ? block.previousElementSibling : block.nextElementSibling
    if (!neighbor || !this.isMergeableBlock(neighbor)) return this.disarmBoundaryMerge(false)
    if (this.blockStyle(neighbor) === this.blockStyle(block)) return this.disarmBoundaryMerge(false)

    const atBoundary = isBackspace
      ? this.caretTouchesBlockEdge(block, range, 'start')
      : this.caretTouchesBlockEdge(block, range, 'end')
    if (!atBoundary) return this.disarmBoundaryMerge(false)

    if (!this.hasContent(block) || !this.hasContent(neighbor)) return this.disarmBoundaryMerge(false)

    // OS key-repeat from a held-down key fires further keydowns for the same key without the
    // user ever releasing it — that isn't a deliberate second press, so it must never complete
    // the confirmation. Keep suppressing the native merge without advancing (or losing) the arm.
    if (event.repeat) {
      event.preventDefault()
      return true
    }

    if (this.isArmedBoundaryMerge(block, neighbor, event.key)) {
      this.disarmBoundaryMerge(false)
      event.preventDefault()

      const first = isBackspace ? neighbor : block
      const second = isBackspace ? block : neighbor
      const firstIndex = Array.from(this.editorTarget.children).indexOf(first)
      const originalHTML = this.editorTarget.innerHTML
      const seam = {
        topLevelNodeCount: first.childNodes.length,
        textOffset: first.textContent.length,
        adjacentTextNodes: first.lastChild?.nodeType === Node.TEXT_NODE &&
          second.firstChild?.nodeType === Node.TEXT_NODE
      }
      const mergedHTML = first.innerHTML + second.innerHTML
      const prepared = this.prepareBoundaryMerge(first, second)
      if (!prepared.first || !prepared.second) {
        this.rollbackBoundaryMerge(originalHTML, prepared.formatSteps, firstIndex, isBackspace)
        return true
      }

      const replacement = this.replaceBoundaryWithParagraph(prepared, mergedHTML)
      if (!replacement.succeeded || !replacement.merged) {
        const historySteps = prepared.formatSteps + (replacement.succeeded ? 1 : 0)
        this.rollbackBoundaryMerge(originalHTML, historySteps, firstIndex, isBackspace)
        return true
      }

      this.placeCaretAtBoundary(replacement.merged, seam)
      this.boundaryMergeHistory = { state: 'applied', companionSteps: prepared.formatSteps }
      this.syncContentChange()
      return true
    }

    event.preventDefault()
    this.armedBoundaryMerge = { block, neighbor, key: event.key }
    return true
  }

  isArmedBoundaryMerge(block, neighbor, key) {
    const armed = this.armedBoundaryMerge
    return !!armed && armed.block === block && armed.neighbor === neighbor && armed.key === key
  }

  disarmBoundaryMerge(result) {
    this.armedBoundaryMerge = null
    return result
  }

  disarmBoundaryMergeOnClick() {
    this.armedBoundaryMerge = null
  }

  // Normalizes DIV to P for style-comparison purposes: a p/div boundary looks identical to the
  // author either way, so it should never trigger the two-step confirmation.
  blockStyle(element) {
    return element.tagName === 'DIV' ? 'P' : element.tagName
  }

  hasContent(element) {
    return element.textContent.trim() !== ''
  }

  // Change the two blocks to the same semantic type before replacing their boundary.
  // formatBlock is a browser editing command, so unlike direct DOM replacement it participates in
  // contenteditable history and preserves inline markup. The subsequent inline insertion therefore
  // lands in a paragraph rather than inside a heading.
  prepareBoundaryMerge(first, second) {
    const parent = first.parentElement
    if (!parent || parent !== second.parentElement) return { formatSteps: 0 }

    const firstIndex = Array.from(parent.children).indexOf(first)
    if (firstIndex < 0 || parent.children[firstIndex + 1] !== second) return { formatSteps: 0 }

    const selection = window.getSelection()
    let formatSteps = 0
    this.preparingBoundaryMerge = true
    try {
      const blockIndexes = [firstIndex, firstIndex + 1]
      blockIndexes.forEach((index) => {
        const element = parent.children[index]
        if (!element || this.blockStyle(element) === 'P') return

        const blockRange = document.createRange()
        blockRange.selectNodeContents(element)
        blockRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(blockRange)
        if (document.execCommand('formatBlock', false, '<p>')) formatSteps += 1
      })
    } finally {
      this.preparingBoundaryMerge = false
    }
    if (formatSteps === 0) return { formatSteps }

    const preparedFirst = parent.children[firstIndex]
    const preparedSecond = parent.children[firstIndex + 1]
    if (!preparedFirst || !preparedSecond) return { formatSteps }
    if (this.blockStyle(preparedFirst) !== 'P' || this.blockStyle(preparedSecond) !== 'P') {
      return { formatSteps }
    }

    return { first: preparedFirst, second: preparedSecond, formatSteps }
  }

  replaceBoundaryWithParagraph(prepared, mergedHTML) {
    const selection = window.getSelection()
    const range = document.createRange()
    range.setStartBefore(prepared.first)
    range.setEndAfter(prepared.second)
    selection.removeAllRanges()
    selection.addRange(range)

    let succeeded = false
    this.preparingBoundaryMerge = true
    try {
      succeeded = document.execCommand('insertHTML', false, mergedHTML)
    } finally {
      this.preparingBoundaryMerge = false
    }
    if (!succeeded) return { succeeded: false, merged: null }

    const insertedRange = selection.rangeCount > 0 ? selection.getRangeAt(0) : null
    return {
      succeeded: true,
      merged: insertedRange && this.enclosingTextBlock(insertedRange)
    }
  }

  rollbackBoundaryMerge(originalHTML, historySteps, firstIndex, isBackspace) {
    this.preparingBoundaryMerge = true
    try {
      for (let step = 0; step < historySteps; step += 1) document.execCommand('undo')
    } finally {
      this.preparingBoundaryMerge = false
    }

    if (this.editorTarget.innerHTML !== originalHTML) this.editorTarget.innerHTML = originalHTML

    const first = this.editorTarget.children[firstIndex]
    const second = this.editorTarget.children[firstIndex + 1]
    const target = isBackspace ? second : first
    if (target) {
      const range = document.createRange()
      range.selectNodeContents(target)
      range.collapse(isBackspace)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
    }

    this.boundaryMergeHistory = null
    this.syncContentChange()
  }

  placeCaretAtBoundary(block, seam) {
    if (!seam.adjacentTextNodes && seam.topLevelNodeCount < block.childNodes.length) {
      const range = document.createRange()
      range.setStartAfter(block.childNodes[seam.topLevelNodeCount - 1])
      range.collapse(true)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
      return
    }

    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT)
    let remaining = seam.textOffset
    let node = walker.nextNode()

    while (node) {
      if (remaining <= node.textContent.length) {
        const range = document.createRange()
        range.setStart(node, remaining)
        range.collapse(true)
        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(range)
        return
      }

      remaining -= node.textContent.length
      node = walker.nextNode()
    }
  }

  // formatBlock and insertHTML are separate browser history entries. When Undo/Redo is the very next
  // edit, consume the companion entry asynchronously after Chrome finishes the first history event,
  // so the confirmed merge behaves like one user action. Any intervening ordinary input clears
  // boundaryMergeHistory in handleEditorInput rather than risking an over-eager undo.
  coalesceBoundaryMergeHistory(event) {
    const history = this.boundaryMergeHistory
    const isUndo = event.inputType === 'historyUndo' && history?.state === 'applied'
    const isRedo = event.inputType === 'historyRedo' && history?.state === 'undone'
    if (!isUndo && !isRedo) return false

    const command = isUndo ? 'undo' : 'redo'
    const nextState = isUndo ? 'undone' : 'applied'
    this.coalescingBoundaryMergeHistory = true

    this.runBoundaryMergeHistoryCommands(command, history.companionSteps, (succeeded) => {
      this.coalescingBoundaryMergeHistory = false
      this.boundaryMergeHistory = succeeded ? { ...history, state: nextState } : null
      this.syncContentChange()
    })

    return true
  }

  runBoundaryMergeHistoryCommands(command, remaining, done) {
    this.boundaryMergeHistoryTimer = setTimeout(() => {
      const succeeded = document.execCommand(command)
      if (!succeeded || remaining <= 1) {
        this.boundaryMergeHistoryTimer = null
        done(succeeded)
        return
      }

      this.runBoundaryMergeHistoryCommands(command, remaining - 1, done)
    }, 0)
  }

  // Excludes anything richer than inline formatting — an image, or a pasted node carrying
  // structural content (table/list) that handlePaste's sanitizeHtml lets through untouched
  // (it only strips script/style and unsafe attributes, not disallowed tags). Rewrapping a
  // <table> inside a fresh <p> would produce HTML the browser doesn't accept as written and
  // silently reparents, so those boundaries are left to native behavior instead.
  isMergeableBlock(element) {
    if (!TEXT_BLOCK_TAGS.includes(element.tagName)) return false

    return !element.querySelector('img, table, ul, ol')
  }

  // Whether the collapsed caret in `range` sits at the block's start (nothing but empty
  // inline content before it) or end (nothing but empty inline content after it). Checks the
  // cloned boundary's text and looks for a <br>/<img> directly, rather than comparing
  // Range.toString(), since toString() ignores those — a caret sitting right after a leading
  // <br> would otherwise read as "block start". An empty inline wrapper (e.g. a leftover
  // <strong></strong> from a formatting toggle) contributes neither and is correctly ignored.
  caretTouchesBlockEdge(block, range, edge) {
    const markerRange = document.createRange()
    markerRange.selectNodeContents(block)
    if (edge === 'start') {
      markerRange.setEnd(range.startContainer, range.startOffset)
    } else {
      markerRange.setStart(range.startContainer, range.startOffset)
    }

    const contents = markerRange.cloneContents()
    return contents.textContent === '' && !contents.querySelector('br, img')
  }

  // Shared selection/collapsed/containment preamble for keydown handlers that only act on a
  // collapsed caret inside the editor. Returns the live Range, or null when the current
  // selection doesn't qualify.
  collapsedCaretRange() {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return null

    const range = selection.getRangeAt(0)
    return this.editorTarget.contains(range.startContainer) ? range : null
  }

  shortcutAction(event) {
    if (!(event.metaKey || event.ctrlKey)) return null

    const key = event.key.toLowerCase()
    const digit = event.code?.startsWith('Digit') ? event.code.slice('Digit'.length) : key
    const plainShortcut = !event.altKey && !event.shiftKey
    const shiftShortcut = event.shiftKey && !event.altKey
    const altShortcut = event.altKey && !event.shiftKey

    if (plainShortcut) {
      return {
        b: 'bold',
        i: 'italic',
        u: 'underline',
        k: 'insertLink',
        ',': 'subscript',
        '.': 'superscript'
      }[key] || null
    }

    if (shiftShortcut) {
      return {
        '7': 'orderedList',
        '8': 'unorderedList'
      }[digit] || null
    }

    if (altShortcut) {
      return {
        '0': 'paragraph',
        '1': 'heading1',
        '2': 'heading2',
        '3': 'heading3',
        '4': 'heading4'
      }[digit] || null
    }

    return null
  }

  // Google Docs-style input rules: a marker followed by Space at the beginning
  // of a text block becomes a real list. Existing list items and mid-line
  // markers are deliberately left alone.
  applyListInputRule(event) {
    if (event.key !== ' ' || event.metaKey || event.ctrlKey || event.altKey) return false

    const range = this.collapsedCaretRange()
    if (!range) return false

    const element = range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer.parentElement
      : range.startContainer
    if (element?.closest('li, ul, ol')) return false

    const block = element?.closest('p, div, h1, h2, h3, h4')
    const root = block && this.editorTarget.contains(block) ? block : this.editorTarget
    const markerRange = document.createRange()
    markerRange.selectNodeContents(root)
    markerRange.setEnd(range.startContainer, range.startOffset)

    const command = {
      '-': 'insertUnorderedList',
      '*': 'insertUnorderedList',
      '1.': 'insertOrderedList'
    }[markerRange.toString()]
    if (!command) return false

    event.preventDefault()
    markerRange.deleteContents()
    document.execCommand(command, false, null)
    this.syncContentChange()
    return true
  }

  syncContent() {
    this.inputTarget.value = this.serializedValue()
    this.updatePlaceholderState()
  }

  // The value persisted to the hidden input: the editor content, wrapped in the
  // tint element when a container background is set. Keeping the tint INSIDE the
  // value means every host that saves/renders the editor's HTML carries it — no
  // per-host persistence code. Matches Sanitizers::RichTextSanitizer's wrapper
  // byte-for-byte so a saved block round-trips without a spurious change.
  serializedValue() {
    const inner = this.editorTarget.innerHTML
    if (!this.currentBackground) return inner

    return `<div class="rte-tint" style="background-color: ${this.currentBackground}">${inner}</div>`
  }

  // Reads a saved tint wrapper out of the loaded value: records its color, unwraps
  // it for editing (display only — the hidden input keeps the server value as the
  // dirty-tracking baseline), and styles the editor + toolbar knob to match.
  // Also called by hosts that REPLACE the editor's content after connect (the
  // presenter-note composer reuses one editor for every note), so the surface and the
  // knob are painted from the value being loaded — including clearing them for a value
  // with no tint, which is why the paint sits outside the wrapper branch.
  hydrateBackground() {
    this.currentBackground = ""

    const wrapper = this.editorTarget.children.length === 1 ? this.editorTarget.children[0] : null
    if (wrapper && wrapper.classList.contains("rte-tint")) {
      this.currentBackground = this.readHexColor(wrapper.getAttribute("style"))
      wrapper.replaceWith(...wrapper.childNodes)
    }

    this.editorTarget.style.backgroundColor = this.currentBackground
    this.updateBackgroundSwatch(this.currentBackground)
  }

  readHexColor(style) {
    const match = /background-color:\s*(#[0-9a-fA-F]{3,6})/.exec(style || "")
    return match ? match[1].toLowerCase() : ""
  }

  updatePlaceholderState() {
    // An image is content even though it contributes no text, so the text-only tests below would
    // call an image-only editor empty and paint the placeholder straight over the picture.
    const hasImage = !!this.editorTarget.querySelector('img')
    const isEmpty = !hasImage &&
                    (this.editorTarget.innerHTML.trim() === '' ||
                     this.editorTarget.innerHTML === '<br>' ||
                     this.editorTarget.textContent.trim() === '')

    this.editorTarget.classList.toggle('is-empty', isEmpty)
  }

  handleFocus() {
    this.editorTarget.classList.add('is-focused')
    this.refreshToolbarState()
  }

  handleBlur() {
    this.armedBoundaryMerge = null
    this.editorTarget.classList.remove('is-focused')
    this.syncContent()
    this.refreshToolbarState()
  }

  handlePaste(event) {
    event.preventDefault()
    const text = event.clipboardData.getData('text/html') || event.clipboardData.getData('text/plain')
    
    // Sanitize and insert
    const sanitized = this.sanitizeHtml(text)
    document.execCommand('insertHTML', false, sanitized)
    this.syncContentChange()
  }

  preserveSelection(event) {
    event.preventDefault()
    if (event.target.closest('.link-dropdown, .link-menu')) this.saveLinkSelection()
    if (event.target.closest('.text-color-dropdown, .text-color-menu')) this.saveTextColorSelection()
  }

  sanitizeHtml(html) {
    const temp = document.createElement('div')
    temp.innerHTML = html

    temp.querySelectorAll('script, style').forEach(el => el.remove())
    temp.querySelectorAll('*').forEach(el => {
      Array.from(el.attributes).forEach(attr => {
        if (this.keepsPastedAttribute(el, attr)) return
        if (!this.allowedPasteAttribute(el, attr.name)) el.removeAttribute(attr.name)
      })
    })

    return temp.innerHTML
  }

  // Matches Sanitizers::RichTextSanitizer: links keep href/target/rel, colored
  // spans/fonts keep the color they arrived with, everything else drops
  // attributes (and on* handlers never survive).
  allowedPasteAttribute(element, name) {
    if (name.startsWith('on')) return false

    if (element.tagName === 'A' && LINK_ATTRIBUTE_ALLOWLIST.includes(name)) return true
    if ((element.tagName === 'SPAN' || element.tagName === 'FONT') && name === 'style') return true
    if (element.tagName === 'FONT' && name === 'color') return true

    return false
  }

  // Without this, copying an image from one part of the note and pasting it into another silently
  // destroys it — the rule above strips every attribute off a non-link, src included. Only for a
  // host that enabled images, and only for an ActiveStorage blob redirect: this is not
  // paste-to-upload, it is not losing an image the editor itself just inserted.
  keepsPastedAttribute(element, attribute) {
    if (!this.imagesValue || element.tagName !== 'IMG') return false
    if (attribute.name === 'alt') return true

    return attribute.name === 'src' && /^\/rails\/active_storage\/blobs\/redirect\/[^/\s]+\//.test(attribute.value)
  }

  bold(event) {
    event.preventDefault()
    this.withUnweightedSurface(() => document.execCommand('bold', false, null))
    this.editorTarget.focus()
    this.syncContentChange()
  }

  // The browser judges bold from the selection's COMPUTED weight, so a surface
  // that renders semi-bold by design (the flip card face) reports never-formatted
  // text as already bold: Bold then un-bolds it into a <span style="font-weight:
  // normal"> the sanitizer strips, and the edit vanishes on save. Reading the
  // state and running the command against a momentarily unweighted surface makes
  // the browser judge boldness by the selection's markup alone — so Bold adds and
  // removes <b>/<strong> tags. A surface with a normal weight is untouched.
  withUnweightedSurface(operation) {
    if (!this.surfaceReadsAsBold()) return operation()

    const inlineWeight = this.editorTarget.style.fontWeight
    this.editorTarget.style.fontWeight = 'normal'

    try {
      return operation()
    } finally {
      this.editorTarget.style.fontWeight = inlineWeight
    }
  }

  surfaceReadsAsBold() {
    const weight = parseInt(window.getComputedStyle(this.editorTarget).fontWeight, 10)

    return Number.isFinite(weight) && weight >= BOLD_WEIGHT_THRESHOLD
  }

  italic(event) {
    event.preventDefault()
    document.execCommand('italic', false, null)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  underline(event) {
    event.preventDefault()
    document.execCommand('underline', false, null)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  subscript(event) {
    this.toggleInlineCommand(event, 'subscript')
  }

  superscript(event) {
    this.toggleInlineCommand(event, 'superscript')
  }

  unorderedList(event) {
    this.toggleList(event, 'insertUnorderedList')
  }

  orderedList(event) {
    this.toggleList(event, 'insertOrderedList')
  }

  heading1(event) {
    this.formatBlock(event, 'h1')
  }

  heading2(event) {
    this.formatBlock(event, 'h2')
  }

  heading3(event) {
    this.formatBlock(event, 'h3')
  }

  heading4(event) {
    this.formatBlock(event, 'h4')
  }

  // Converts the current block back to a plain paragraph — the "Normal text"
  // toolbar button, to undo a heading and return the block to default styling.
  // Unlike the heading buttons, this always normalizes the WHOLE block (never
  // splits a partial selection), since "make this a normal paragraph" is a
  // block-level intent, not a "promote these words" one.
  paragraph(event) {
    event.preventDefault()
    document.execCommand('formatBlock', false, '<p>')
    this.editorTarget.focus()
    this.syncContentChange()
  }

  formatBlock(event, tagName) {
    event.preventDefault()
    this.applyBlockFormat(tagName)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  // Applies a block-level format (heading or paragraph). A block tag like <h2>
  // cannot cover only part of a line, so when the user selected a sub-range of a
  // single text block we SPLIT it: the selected text becomes its own heading and
  // the surrounding text stays as paragraphs — instead of retagging the whole
  // paragraph. A caret (collapsed), a full-block selection, a multi-block
  // selection, or a list/table falls back to the browser's whole-block command.
  applyBlockFormat(tagName) {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      document.execCommand('formatBlock', false, `<${tagName}>`)
      return
    }

    const range = selection.getRangeAt(0)
    const block = this.enclosingTextBlock(range)
    const isPartial = block && !range.collapsed && block.tagName.toLowerCase() !== tagName

    if (!isPartial) {
      document.execCommand('formatBlock', false, `<${tagName}>`)
      return
    }

    this.splitBlockAroundSelection(block, range, tagName)
  }

  // The single top-level text block (p/h1-4/div, a direct child of the editor)
  // that fully contains the selection, or null when the selection is outside the
  // editor, spans more than one block, or sits in a list/table.
  enclosingTextBlock(range) {
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return null

    const startBlock = this.topLevelBlock(range.startContainer)
    const endBlock = this.topLevelBlock(range.endContainer)
    if (!startBlock || startBlock !== endBlock) return null
    if (!TEXT_BLOCK_TAGS.includes(startBlock.tagName)) return null

    return startBlock
  }

  // Walks up to the node's ancestor that is a direct child of the editor, or null
  // when the node is the editor itself or lives outside it.
  topLevelBlock(node) {
    let element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node
    if (!element || element === this.editorTarget || !this.editorTarget.contains(element)) return null

    while (element.parentElement && element.parentElement !== this.editorTarget) {
      element = element.parentElement
    }
    return element.parentElement === this.editorTarget ? element : null
  }

  // Rebuilds one text block as up to three siblings: the text before the
  // selection (kept in the original tag), the selected text (in the new tag),
  // and the text after (kept in the original tag). Empty leading/trailing parts
  // are dropped, and the caret is left inside the new heading.
  splitBlockAroundSelection(block, range, tagName) {
    const originalTag = block.tagName.toLowerCase()

    const beforeRange = document.createRange()
    beforeRange.selectNodeContents(block)
    beforeRange.setEnd(range.startContainer, range.startOffset)

    const afterRange = document.createRange()
    afterRange.selectNodeContents(block)
    afterRange.setStart(range.endContainer, range.endOffset)

    const heading = this.wrapFragment(range.cloneContents(), tagName)
    if (!heading) {
      document.execCommand('formatBlock', false, `<${tagName}>`)
      return
    }

    const nodes = []
    const before = this.wrapFragment(beforeRange.cloneContents(), originalTag)
    if (before) nodes.push(before)
    nodes.push(heading)
    const after = this.wrapFragment(afterRange.cloneContents(), originalTag)
    if (after) nodes.push(after)

    block.replaceWith(...nodes)

    const restored = document.createRange()
    restored.selectNodeContents(heading)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(restored)
  }

  // Wraps a cloned fragment in `tagName`, or returns null when the fragment holds
  // no meaningful content (so an empty before/after part is skipped).
  wrapFragment(fragment, tagName) {
    if ((fragment.textContent || '').trim() === '' && !fragment.querySelector('img, br')) {
      return null
    }
    const element = document.createElement(tagName)
    element.appendChild(fragment)
    return element
  }

  toggleInlineCommand(event, command) {
    event.preventDefault()
    document.execCommand(command, false, null)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  toggleList(event, command) {
    event.preventDefault()
    document.execCommand(command, false, null)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  insertLink(event) {
    event.preventDefault()
    this.saveLinkSelection()
    this.prefillLinkForm()
    this.revealBubbleToolbarForLink()
    this.linkDropdownInstance()?.show()
  }

  // Cmd+K can fire with a caret inside a link, when the bubble is hidden. The
  // popover hangs off that toolbar, so it has to be on-screen before the menu
  // opens — otherwise Bootstrap positions against a display:none toggle.
  revealBubbleToolbarForLink() {
    if (!this.bubbleToolbarValue) return

    const toolbar = this.toolbarElement
    if (!toolbar || !this.savedLinkRange) return

    toolbar.classList.add('is-visible')
    this.positionBubbleToolbar(toolbar)
  }

  handleLinkMenuShow() {
    this.saveLinkSelection()
  }

  handleLinkMenuShown() {
    this.prefillLinkForm()
    this.focusLinkUrl()
  }

  submitLinkOnEnter(event) {
    if (event.key !== 'Enter') return

    event.preventDefault()
    this.applyLink(event)
  }

  applyLink(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasLinkUrlTarget) return

    const url = this.linkUrlTarget.value.trim()
    if (!url) return

    try {
      applyLinkToRange(this.savedLinkRange, this.editorTarget, url, this.linkOpensInNewTab())
      this.syncContentChange()
    } finally {
      this.linkDropdownInstance()?.hide()
    }
  }

  saveLinkSelection() {
    const nextRange = cloneLiveRange(
      this.savedLinkRange,
      (range) => rangeIsInRoot(range, this.editorTarget)
    )
    if (nextRange === this.savedLinkRange) return

    this.savedLinkRange = nextRange
    this.savedExistingLink = linkFromRange(nextRange)
  }

  prefillLinkForm() {
    if (!this.hasLinkUrlTarget) return

    const link = this.savedExistingLink || linkFromRange(this.savedLinkRange)
    this.linkUrlTarget.value = link?.getAttribute('href') || 'https://'
    if (this.hasLinkNewTabTarget) this.linkNewTabTarget.checked = link?.getAttribute('target') === '_blank'
  }

  focusLinkUrl() {
    if (!this.hasLinkUrlTarget) return

    this.linkUrlTarget.focus()
    this.linkUrlTarget.select()
  }

  linkOpensInNewTab() {
    return this.hasLinkNewTabTarget && this.linkNewTabTarget.checked
  }

  linkDropdownInstance() {
    if (!this.hasLinkToggleTarget) return null

    return Dropdown.getOrCreateInstance(this.linkToggleTarget, {
      popperConfig: (defaultConfig) => ({ ...defaultConfig, strategy: "fixed" })
    })
  }

  // --- Inline image upload (opt-in; the host passes images: true to the component) --------------

  // Unlike insertLink, this has to REMEMBER where the caret was. preserveSelection only calls
  // preventDefault() on mousedown, which is enough to keep the live selection alive for a
  // synchronous execCommand — but the file dialog and the upload that follows are neither, and by
  // the time the blob comes back the selection is long gone.
  insertImage(event) {
    event.preventDefault()
    this.savedImageRange = this.currentEditorRange()
    this.imageInputTarget.click()
  }

  // Abandons an upload still in flight. A host that swaps the editor's content out from under it
  // (the presenter-note composer does exactly this — it reuses ONE editor element and assigns
  // innerHTML when it reopens on a different note) must call this, or the upload finishes against
  // the editor as it is by THEN and drops the image into the wrong note's body.
  cancelPendingImage() {
    this.imageUploadToken = (this.imageUploadToken || 0) + 1
    this.savedImageRange = null
    this.setImageBusy(false)
    this.showImageStatus("")
  }

  imageSelected() {
    const file = this.imageInputTarget.files[0]
    if (!file) return

    const rejection = this.imageRejection(file)
    if (rejection) {
      this.showImageStatus(rejection, { error: true })
      this.imageInputTarget.value = ""
      return
    }

    this.setImageBusy(true)
    this.showImageStatus("Uploading image…")

    const token = this.imageUploadToken || 0
    new DirectUpload(file, this.directUploadUrlValue).create((error, blob) => {
      // Reset regardless of outcome, so re-picking the SAME file still fires `change`.
      this.imageInputTarget.value = ""
      // The editor was reloaded (or another pick started) while this was in flight — whatever it
      // returns belongs to content that is no longer on screen.
      if (token !== (this.imageUploadToken || 0)) return

      this.setImageBusy(false)

      if (error) {
        this.showImageStatus("Image upload failed. Try again.", { error: true })
        return
      }

      this.insertImageBlob(blob)
      this.showImageStatus("")
    })
  }

  imageRejection(file) {
    if (file.size > MAX_IMAGE_BYTES) return "Images must be 15 MB or smaller."
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return "Choose a PNG, JPEG, GIF or WebP image."

    return null
  }

  // The src is ActiveStorage's own blob redirect — the one shape the server allowlist accepts (see
  // Sanitizers::RichTextSanitizer::BLOB_SRC_PATTERN). The filename is encoded because a space in it
  // would otherwise produce a src the allowlist rejects, and the image would vanish on save.
  insertImageBlob(blob) {
    const src = `/rails/active_storage/blobs/redirect/${blob.signed_id}/${encodeURIComponent(blob.filename)}`
    this.restoreImageRange()
    // The trailing paragraph is what leaves the caret on a fresh line UNDER the image, so typing
    // continues below it rather than beside it.
    document.execCommand("insertHTML", false, `<img src="${src}" alt=""><p><br></p>`)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  currentEditorRange() {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return null

    const range = selection.getRangeAt(0)

    return this.editorTarget.contains(range.commonAncestorContainer) ? range.cloneRange() : null
  }

  // Puts the caret back where the author left it — or at the end of the content when the editor was
  // never focused, so clicking the button first still inserts somewhere sensible.
  //
  // The saved range is only trusted while its nodes are still in the editor. A host that reloads the
  // editor mid-upload (the composer does exactly this — it reopens on another note by assigning
  // innerHTML) detaches them, and inserting against a stale range would drop the image into whatever
  // is focused by then, i.e. silently into the WRONG note.
  restoreImageRange() {
    const range = document.createRange()
    if (this.savedRangeIsLive()) {
      range.setStart(this.savedImageRange.startContainer, this.savedImageRange.startOffset)
      range.setEnd(this.savedImageRange.endContainer, this.savedImageRange.endOffset)
    } else {
      range.selectNodeContents(this.editorTarget)
      range.collapse(false)
    }

    this.editorTarget.focus()
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    this.savedImageRange = null
  }

  savedRangeIsLive() {
    const range = this.savedImageRange

    return !!range &&
      range.startContainer.isConnected &&
      this.editorTarget.contains(range.startContainer) &&
      this.editorTarget.contains(range.endContainer)
  }

  showImageStatus(message, { error = false } = {}) {
    if (!this.hasImageStatusTarget) return

    this.imageStatusTarget.textContent = message
    this.imageStatusTarget.classList.toggle("is-error", error)
    this.imageStatusTarget.hidden = message === ""
  }

  setImageBusy(busy) {
    if (!this.hasImageButtonTarget) return

    this.imageButtonTarget.classList.toggle("is-busy", busy)
    this.imageButtonTarget.setAttribute("aria-disabled", busy ? "true" : "false")
  }

  // Sets the background color of the whole editor container (not the selection).
  // An empty data-color clears the fill back to the default white. The color is
  // folded into the serialized value (syncContent), so the change persists and
  // re-renders wherever the editor's HTML is stored/shown.
  setBackgroundColor(event) {
    event.preventDefault()
    this.currentBackground = (event.currentTarget.dataset.color || '').toLowerCase()
    this.editorTarget.style.backgroundColor = this.currentBackground
    this.updateBackgroundSwatch(this.currentBackground)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  updateBackgroundSwatch(color) {
    if (!this.hasBackgroundSwatchTarget) return
    this.backgroundSwatchTarget.style.backgroundColor = color || ''
  }

  // Paints the selected text with a palette hex, or unwraps a previously applied
  // color. The swatch click collapses the live highlight, so the range saved on
  // mousedown is what the command actually paints.
  setTextColor(event) {
    event.preventDefault()
    this.restoreTextColorSelection()
    const color = (event.currentTarget.dataset.color || '').toLowerCase()
    this.editorTarget.focus()
    if (color) {
      document.execCommand('foreColor', false, color)
    } else {
      this.clearTextColor()
    }
    this.syncContentChange()
  }

  saveTextColorSelection() {
    this.savedTextColorRange = cloneLiveRange(
      this.savedTextColorRange,
      (range) => rangeIsInRoot(range, this.editorTarget)
    )
  }

  restoreTextColorSelection() {
    this.saveTextColorSelection()
    if (!this.savedTextColorRange) return

    this.editorTarget.focus()
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(this.savedTextColorRange)
  }

  clearTextColor() {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    this.colorNodesInRange(range).forEach((node) => this.unwrapTextColor(node))
  }

  colorNodesInRange(range) {
    const nodes = new Set()
    const root = range.commonAncestorContainer
    let element = root.nodeType === Node.TEXT_NODE ? root.parentElement : root
    if (!element || !this.editorTarget.contains(element)) return []

    while (element && element !== this.editorTarget) {
      if (this.carriesTextColor(element)) nodes.add(element)
      element = element.parentElement
    }

    const scope = root.nodeType === Node.ELEMENT_NODE ? root : root.parentElement
    if (scope && this.editorTarget.contains(scope)) {
      scope.querySelectorAll('span, font').forEach((node) => {
        if (range.intersectsNode(node) && this.carriesTextColor(node)) nodes.add(node)
      })
    }

    return Array.from(nodes)
  }

  carriesTextColor(node) {
    if (!node.matches?.('span, font')) return false

    return Boolean(this.readTextColor(node) || /(?:^|;)\s*color\s*:/i.test(node.getAttribute('style') || ''))
  }

  unwrapTextColor(node) {
    const cleaned = (node.getAttribute('style') || '')
      .split(';')
      .map((part) => part.trim())
      .filter((part) => part && !/^color\s*:/i.test(part))
      .join('; ')

    node.removeAttribute('color')
    if (cleaned) {
      node.setAttribute('style', cleaned)
    } else {
      node.removeAttribute('style')
    }
    node.classList.remove('rte-ink')

    if (node.tagName !== 'FONT' && (node.tagName !== 'SPAN' || node.attributes.length > 0)) return

    const parent = node.parentNode
    if (!parent) return

    while (node.firstChild) parent.insertBefore(node.firstChild, node)
    parent.removeChild(node)
  }

  updateTextColorSwatch(color) {
    if (!this.hasTextColorSwatchTarget) return
    this.textColorSwatchTarget.style.color = color || ''
  }

  selectionTextColor() {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return ''

    const node = selection.anchorNode
    let element = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node
    if (!element || !this.editorTarget.contains(element)) return ''

    while (element && element !== this.editorTarget) {
      const color = this.readTextColor(element)
      if (color) return color
      element = element.parentElement
    }

    return ''
  }

  readTextColor(element) {
    const fromAttribute = this.normalizeCssColor(element.getAttribute('color'))
    if (fromAttribute) return fromAttribute

    const match = /(?:^|;)\s*color:\s*([^;]+)/i.exec(element.getAttribute('style') || '')
    return match ? this.normalizeCssColor(match[1].trim()) : ''
  }

  normalizeCssColor(value) {
    if (!value) return ''

    const color = value.trim().toLowerCase()
    if (/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/.test(color)) return color

    const rgb = color.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/)
    if (!rgb) return ''

    const channels = rgb.slice(1, 4).map((channel) => Number(channel))
    if (channels.some((channel) => channel > 255)) return ''

    return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`
  }

  insertPlaceholder(event) {
    event.preventDefault()
    const placeholder = event.currentTarget.dataset.placeholder
    
    // Focus the editor
    this.editorTarget.focus()
    
    // Insert the placeholder at cursor position
    document.execCommand('insertText', false, placeholder)
    this.syncContentChange()
  }

  syncContentChange() {
    this.syncContent()
    this.refreshToolbarState()
    this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
  }

  refreshToolbarState() {
    const commands = this.activeCommands()

    this.toolbarButtons.forEach((button) => {
      const active = commands.has(button.dataset.richTextEditorCommand)
      button.classList.toggle('is-active', active)
      button.setAttribute('aria-pressed', active ? 'true' : 'false')
    })

    this.updateTextColorSwatch(this.selectionTextColor())
    this.updateBubbleToolbar()
  }

  // While the pointer is dragging out a selection the bubble stays hidden — it
  // would otherwise chase the cursor and swallow the mouseup that ends the drag.
  // It appears once the pointer is released (or on a keyboard selection).
  handlePointerDown() {
    this.pointerSelectionInProgress = true
  }

  handlePointerUp() {
    if (!this.pointerSelectionInProgress) return

    this.pointerSelectionInProgress = false
    this.updateBubbleToolbar()
  }

  updateBubbleToolbar() {
    if (!this.bubbleToolbarValue) return

    const toolbar = this.toolbarElement
    if (!toolbar) return

    if (this.shouldShowBubbleToolbar()) {
      this.positionBubbleToolbar(toolbar)
    } else if (!this.bubbleToolbarEngaged(toolbar)) {
      toolbar.classList.remove('is-visible')
    }
  }

  shouldShowBubbleToolbar() {
    if (this.pointerSelectionInProgress || !this.editorTarget.isContentEditable) return false

    const selection = window.getSelection()
    return Boolean(
      selection &&
      selection.rangeCount > 0 &&
      !selection.isCollapsed &&
      this.editorTarget.contains(selection.anchorNode) &&
      this.editorTarget.contains(selection.focusNode) &&
      selection.toString().trim() !== ''
    )
  }

  // The selection collapses when a toolbar control takes focus (e.g. opening the
  // background-color dropdown) — keep the bubble up while the user is mid-action.
  bubbleToolbarEngaged(toolbar) {
    if (toolbar.contains(document.activeElement)) return true

    return Boolean(toolbar.querySelector('.dropdown-menu.show'))
  }

  positionBubbleToolbar(toolbar) {
    const range = window.getSelection().getRangeAt(0)
    const virtualReference = { getBoundingClientRect: () => range.getBoundingClientRect() }
    const boundaryPadding = { top: this.bubbleTopBoundary(), right: 8, bottom: 8, left: 8 }

    toolbar.classList.add('is-visible')
    computePosition(virtualReference, toolbar, {
      placement: 'top',
      middleware: [offset(8), flip({ padding: boundaryPadding }), shift({ padding: boundaryPadding })]
    }).then(({ x, y }) => {
      if (!toolbar.classList.contains('is-visible')) return

      Object.assign(toolbar.style, { left: `${x}px`, top: `${y}px` })
    })
  }

  // A selection near the top of the scroller must flip the bubble BELOW the text
  // instead of sliding it under the app's sticky topbar, so the boundary the
  // bubble flips against starts beneath the topbar rather than at the viewport edge.
  bubbleTopBoundary() {
    const topbar = document.querySelector('.topbar')
    const topbarBottom = topbar ? Math.max(topbar.getBoundingClientRect().bottom, 0) : 0

    return topbarBottom + 8
  }

  activeCommands() {
    const selection = window.getSelection()
    const activeCommands = new Set()

    if (!selection || selection.rangeCount === 0 || !this.editorTarget.contains(selection.anchorNode)) {
      return activeCommands
    }

    this.withUnweightedSurface(() => {
      ;[
        'bold',
        'italic',
        'underline',
        'subscript',
        'superscript',
        'insertUnorderedList',
        'insertOrderedList'
      ].forEach((command) => {
        if (document.queryCommandState(command)) activeCommands.add(command)
      })
    })

    const block = this.closestBlock(selection.anchorNode)
    if (block) activeCommands.add(block.tagName.toLowerCase())

    const inline = this.closestInlineFormat(selection.anchorNode) || this.closestInlineFormat(selection.focusNode)
    if (inline) activeCommands.add(this.commandForInlineFormat(inline))

    return activeCommands
  }

  closestBlock(node) {
    if (!node) return null

    const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node

    return element?.closest?.('h1, h2, h3, h4, p')
  }

  closestInlineFormat(node) {
    if (!node) return null

    const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node

    return element?.closest?.('sub, sup')
  }

  commandForInlineFormat(element) {
    return {
      sub: 'subscript',
      sup: 'superscript'
    }[element.tagName.toLowerCase()]
  }

  get toolbarButtons() {
    return this.element.querySelectorAll('[data-rich-text-editor-command]')
  }
}
